'use client';

import { XCircleIcon } from '@heroicons/react/24/outline';
import Button from 'components/common/Button';
import FocusTrap from 'components/common/FocusTrap';
import SearchBox from 'components/common/SearchBox';
import useDebouncedValue from 'lib/hooks/useDebouncedValue';
import { updateTableFilters } from 'lib/utils/table';
import { useTranslations } from 'next-intl';
import {
  type ChangeEventHandler,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { ColumnId, type HistoryReactTable } from './columns';
import HistoryChainMultiSelect from './HistoryChainMultiSelect';
import HistoryEventTypeMultiSelect from './HistoryEventTypeMultiSelect';

interface Props {
  table: HistoryReactTable;
  isPremium?: boolean;
  ref?: Ref<HistorySearchBoxRef>;
}

export interface HistorySearchBoxRef {
  setInputValue: (value: string) => void;
}

const SPENDER_PREFIX = 'spender:';
const TOKEN_PREFIX = 'token:';
const CHAIN_PREFIX = 'chain:';
const EVENT_PREFIX = 'event:';

interface CategorisedTerms {
  spenderTerms: string[];
  tokenTerms: string[];
  chainTerms: string[];
  eventTerms: string[];
  combinedTerms: string[];
}

const parseSearchTerms = (searchTerm: string): CategorisedTerms => {
  return searchTerm
    .trim()
    .split(',')
    .map((term) => term.trim())
    .filter(Boolean)
    .reduce<CategorisedTerms>(
      (acc, term) => {
        const lowerTerm = term.toLowerCase();

        if (lowerTerm.startsWith(SPENDER_PREFIX)) {
          const value = term.slice(SPENDER_PREFIX.length).trim();
          if (value) acc.spenderTerms.push(value);
        } else if (lowerTerm.startsWith(TOKEN_PREFIX)) {
          const value = term.slice(TOKEN_PREFIX.length).trim();
          if (value) acc.tokenTerms.push(value);
        } else if (lowerTerm.startsWith(CHAIN_PREFIX)) {
          const value = term.slice(CHAIN_PREFIX.length).trim();
          if (value) acc.chainTerms.push(value);
        } else if (lowerTerm.startsWith(EVENT_PREFIX)) {
          const value = term.slice(EVENT_PREFIX.length).trim();
          if (value) acc.eventTerms.push(value);
        } else {
          acc.combinedTerms.push(term);
        }

        return acc;
      },
      { spenderTerms: [], tokenTerms: [], chainTerms: [], eventTerms: [], combinedTerms: [] },
    );
};

const HistorySearchBox = ({ table, isPremium = false, ref }: Props) => {
  const t = useTranslations();
  const [inputValue, setInputValue] = useState<string>('');
  const [searchValue, { flushWith }] = useDebouncedValue(inputValue, 200);

  const chainTerms = useMemo(() => parseSearchTerms(inputValue).chainTerms, [inputValue]);
  const eventTerms = useMemo(() => parseSearchTerms(inputValue).eventTerms, [inputValue]);

  useImperativeHandle(ref, () => ({
    setInputValue,
  }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: table is stable
  useEffect(() => {
    const tableFilters = [];
    const categorisedTerms = parseSearchTerms(searchValue);

    if (categorisedTerms.spenderTerms.length > 0) {
      tableFilters.push({ id: ColumnId.SPENDER, value: categorisedTerms.spenderTerms });
    }

    if (categorisedTerms.tokenTerms.length > 0) {
      tableFilters.push({ id: ColumnId.ASSET, value: categorisedTerms.tokenTerms });
    }

    if (categorisedTerms.combinedTerms.length > 0) {
      tableFilters.push({ id: ColumnId.COMBINED_SEARCH, value: categorisedTerms.combinedTerms });
    }

    if (categorisedTerms.chainTerms.length > 0) {
      tableFilters.push({ id: ColumnId.CHAIN, value: categorisedTerms.chainTerms });
    }

    if (categorisedTerms.eventTerms.length > 0) {
      tableFilters.push({ id: ColumnId.EVENT_TYPE, value: categorisedTerms.eventTerms });
    }

    const ignoreIds = Object.values(ColumnId).filter(
      (id) =>
        id !== ColumnId.SPENDER &&
        id !== ColumnId.ASSET &&
        id !== ColumnId.COMBINED_SEARCH &&
        id !== ColumnId.CHAIN &&
        id !== ColumnId.EVENT_TYPE,
    );

    updateTableFilters(table, tableFilters, ignoreIds);
  }, [searchValue]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setInputValue(event.target.value);
  }, []);

  const replacePrefixedTerms = useCallback(
    (prefix: string, nextTerms: string[]) => {
      const otherTerms = inputValue
        .split(',')
        .map((term) => term.trim())
        .filter(Boolean)
        .filter((term) => !term.toLowerCase().startsWith(prefix));

      const prefixedTerms = nextTerms.map((term) => `${prefix}${term}`);
      const nextInput = [...otherTerms, ...prefixedTerms].join(', ');

      setInputValue(nextInput);
      flushWith(nextInput);
    },
    [flushWith, inputValue],
  );

  const handleChainTermsChange = useCallback(
    (nextChainTerms: string[]) => replacePrefixedTerms(CHAIN_PREFIX, nextChainTerms),
    [replacePrefixedTerms],
  );

  const handleEventTermsChange = useCallback(
    (nextEventTerms: string[]) => replacePrefixedTerms(EVENT_PREFIX, nextEventTerms),
    [replacePrefixedTerms],
  );

  const resetButton = (
    <Button
      style="tertiary"
      onClick={() => {
        setInputValue('');
        flushWith('');
      }}
      size="none"
    >
      <XCircleIcon className="w-6 h-6" />
    </Button>
  );

  return (
    <div className="flex flex-col sm:flex-row gap-2 p-4">
      <SearchBox
        id="history-search"
        onSubmit={(event) => event.preventDefault()}
        onChange={handleChange}
        value={inputValue}
        placeholder={t('address.search.history')}
        className="w-full"
      >
        <FocusTrap />
        {inputValue.trim().length > 0 && resetButton}
      </SearchBox>
      {isPremium ? (
        <>
          <HistoryEventTypeMultiSelect eventTerms={eventTerms} onEventTermsChange={handleEventTermsChange} />
          <HistoryChainMultiSelect chainTerms={chainTerms} onChainTermsChange={handleChainTermsChange} />
        </>
      ) : null}
    </div>
  );
};

export default HistorySearchBox;
